"use client";

import { useEffect, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";

export default function LoginPage() {
  const router = useRouter();
  const sp = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [showPw, setShowPw] = useState(false);
  // 2026-10-06 L4 · 鎖定倒數
  const [lockUntilMs, setLockUntilMs] = useState<number | null>(null);
  const [nowMs, setNowMs] = useState<number>(Date.now());
  useEffect(() => {
    if (lockUntilMs === null) return;
    const t = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(t);
  }, [lockUntilMs]);
  const lockRemainSec = lockUntilMs ? Math.max(0, Math.ceil((lockUntilMs - nowMs) / 1000)) : 0;
  const isLocked = lockRemainSec > 0;

  // mount 時把 sidebar / 全域 padding 收掉 · unmount 恢復
  useEffect(() => {
    document.body.classList.add("login-page");
    return () => document.body.classList.remove("login-page");
  }, []);

  // 記住我：從 localStorage 還原
  const [remember, setRemember] = useState(false);
  const [username, setUsername] = useState("");
  useEffect(() => {
    try {
      const saved = localStorage.getItem("sjc_login_username");
      if (saved) { setUsername(saved); setRemember(true); }
    } catch { /* noop */ }
  }, []);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setLoading(true);
    setErr(null);
    const fd = new FormData(e.currentTarget);
    const u = String(fd.get("username") ?? "").trim();
    const res = await fetch("/api/auth/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username: u, password: fd.get("password") }),
    });
    setLoading(false);
    if (!res.ok) {
      const j = await res.json().catch(() => ({ error: "登入失敗" }));
      setErr(j.error ?? "登入失敗");
      // 2026-10-06 L4 · 429 鎖定時啟動倒數
      if (res.status === 429 && typeof j.retryAfterSeconds === "number" && j.retryAfterSeconds > 0) {
        setLockUntilMs(Date.now() + j.retryAfterSeconds * 1000);
      }
      return;
    }
    // 記住我
    try {
      if (remember) localStorage.setItem("sjc_login_username", u);
      else localStorage.removeItem("sjc_login_username");
    } catch { /* noop */ }
    const next = sp.get("next");
    const safeNext = next && next.startsWith("/") && !next.startsWith("//") ? next : "/";
    // 強制整頁重載（不走 SPA push）· 帶時戳 query 破壞任何中介快取
    // 這樣每次登入都是全新 React state + 全新 JS chunk，避免舊 bundle 殘留造成的 crash
    const sep = safeNext.includes("?") ? "&" : "?";
    window.location.replace(`${safeNext}${sep}_r=${Date.now()}`);
  }

  return (
    <div className="login-root">
      {/* ═══ 左半 · 建築照片 ═══ */}
      <div className="login-hero">
        <img className="bg-photo" src="/login-hero.webp" alt="給臣管理顧問大樓" />
        <div className="hero-content">
          <h1>Building<br/>the future<br/><b>together.</b></h1>
          <div className="hero-divider"></div>
          <div className="hero-tagline">創新 · 專業 · 永續 · 共好</div>
        </div>
        <div className="hero-foot">
          <span>EST. 2026</span>
          <span>TAIPEI</span>
        </div>
      </div>

      {/* ═══ 右半 · 表單 ═══ */}
      <div className="login-form-side">
        <div className="login-brand">
          <div className="logo-wrap">
            <img src="/login-logo.webp" alt="給臣管理顧問有限公司" />
          </div>
        </div>

        <form className="login-form-inner" onSubmit={onSubmit}>
          <h2 className="greet">歡迎回來</h2>
          <p className="greet-sub">請輸入您的帳號與密碼以登入系統</p>

          <div className="login-field">
            <label htmlFor="u">帳號</label>
            <div className="wrap">
              <span className="ic-lead" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                  <circle cx="12" cy="7" r="4"/>
                </svg>
              </span>
              <input
                id="u" name="username" type="text"
                placeholder="請輸入帳號" required autoFocus
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            </div>
          </div>

          <div className="login-field">
            <label htmlFor="p">密碼</label>
            <div className="wrap">
              <span className="ic-lead" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
              </span>
              <input
                id="p" name="password"
                type={showPw ? "text" : "password"}
                placeholder="請輸入密碼" required
              />
              <button className="eye" type="button" aria-label={showPw ? "隱藏密碼" : "顯示密碼"}
                onClick={() => setShowPw((v) => !v)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  {showPw ? (
                    <>
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                      <line x1="1" y1="1" x2="23" y2="23"/>
                    </>
                  ) : (
                    <>
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </>
                  )}
                </svg>
              </button>
            </div>
          </div>

          {err && <div className="login-err">⚠ {err}</div>}
          {/* 2026-10-06 L4 · 鎖定倒數顯示 */}
          {isLocked && (
            <div className="login-err" style={{ background: "#FEF3C7", color: "#92400E", border: "1px solid #F59E0B" }}>
              🔒 帳號鎖定中 · 剩 <b>{Math.floor(lockRemainSec / 60)}:{String(lockRemainSec % 60).padStart(2, "0")}</b> 可再試
            </div>
          )}

          <div className="login-row">
            <label title="勾選後下次會自動填入帳號（不會記住密碼或 session）">
              <input type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)} />
              記住我 <small style={{ color: "#9CA3AF", fontWeight: 400 }}>（下次自動填帳號）</small>
            </label>
          </div>

          <button className="btn-primary" type="submit" disabled={loading || isLocked}>
            {loading ? "驗證中…" : isLocked ? `🔒 ${lockRemainSec}s` : "登入"}
          </button>

          {/* 2026-10-06 L3 · 忘記密碼說明 */}
          <div style={{ marginTop: 10, fontSize: 11, color: "#6B7280", textAlign: "center" }}>
            忘記密碼？請聯絡系統管理員重設
          </div>
        </form>

        <div className="login-foot">
          <div className="links">
            <span className="lang">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <circle cx="12" cy="12" r="10"/>
                <line x1="2" y1="12" x2="22" y2="12"/>
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
              </svg>
              繁體中文
            </span>
            <span className="sep">|</span>
            <span>隱私權政策</span>
            <span className="sep">|</span>
            <span>服務條款</span>
          </div>
          <div className="copyright">© 2026 給臣專案管理 · All rights reserved.</div>
        </div>
      </div>
    </div>
  );
}
